/**
 * Share Modal Component
 * Handles sharing flow for milestone cards
 * Structured for future social platform integrations
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  X,
  Download,
  Copy,
  Share2,
  Instagram,
  Twitter,
  Smartphone,
  Link2,
  Check,
  Image as ImageIcon,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  BadgeEarnedCard,
  LeaderboardRankCard,
  EventsMilestoneCard,
  VIPStatusCard,
  PassportMilestoneCard,
} from './MilestoneCards';

// Social platform config for future integration
const SOCIAL_PLATFORMS = {
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'from-pink-500 via-purple-500 to-orange-400',
    available: false, // Set to true when integration is ready
    shareUrl: null,
  },
  twitter: {
    name: 'Twitter/X',
    icon: Twitter,
    color: 'from-blue-400 to-blue-600',
    available: false,
    shareUrl: (text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  },
  tiktok: {
    name: 'TikTok',
    icon: Smartphone,
    color: 'from-black via-pink-500 to-cyan-400',
    available: false,
    shareUrl: null,
  },
  snapchat: {
    name: 'Snapchat',
    icon: Smartphone,
    color: 'from-yellow-400 to-yellow-500',
    available: false,
    shareUrl: null,
  },
};

// Share action buttons
const ShareActionButton = ({ icon: Icon, label, onClick, disabled, gradient, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'flex flex-col items-center gap-2 p-4 rounded-xl border transition-all',
      disabled 
        ? 'bg-white/[0.02] border-white/[0.04] opacity-50 cursor-not-allowed'
        : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.1]'
    )}
    data-testid={testId}
  >
    <div className={cn(
      'w-12 h-12 rounded-xl flex items-center justify-center',
      gradient ? `bg-gradient-to-br ${gradient}` : 'bg-white/[0.05]'
    )}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <span className="text-xs text-white/70 font-medium">{label}</span>
  </button>
);

// Card type selector
const CardTypeOption = ({ active, onClick, label, icon: Icon }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all',
      active 
        ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
        : 'bg-white/[0.03] text-white/60 border border-white/[0.06] hover:bg-white/[0.06]'
    )}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

export const ShareModal = ({
  isOpen,
  onClose,
  milestoneType = 'badge', // 'badge' | 'rank' | 'events' | 'vip' | 'passport'
  milestoneData = {},
  userName,
}) => {
  const cardRef = useRef(null);
  const [cardSize, setCardSize] = useState('default'); // 'default' | 'story' | 'square'
  const [isExporting, setIsExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Generate share text based on milestone
  const getShareText = useCallback(() => {
    switch (milestoneType) {
      case 'badge':
        return `🏅 Just unlocked the "${milestoneData.badgeName}" badge on CamPassed! #CamPassed #Achievement`;
      case 'rank':
        return `🔥 I'm ranked #${milestoneData.rank} on my campus leaderboard! Top ${milestoneData.percentile || 10}%! #CamPassed #CampusLife`;
      case 'events':
        return `🎉 Just hit ${milestoneData.eventsCount} events attended on CamPassed! #CamPassed #EventLife`;
      case 'vip':
        return `👑 Just earned ${milestoneData.statusName || 'VIP'} status on CamPassed! #CamPassed #VIP`;
      case 'passport':
        return `📍 Milestone unlocked: ${milestoneData.milestoneValue} ${milestoneData.milestoneName || 'achievements'} on CamPassed! #CamPassed`;
      default:
        return `Check out my achievement on CamPassed! #CamPassed`;
    }
  }, [milestoneType, milestoneData]);
  
  // Export card as image (placeholder - would use html2canvas or similar)
  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // For now, show a toast that this feature is coming
      // In production, this would use html2canvas to export the card
      toast.info('Image export coming soon! For now, take a screenshot of the card.');
      
      // Placeholder for future implementation:
      // const canvas = await html2canvas(cardRef.current, {
      //   backgroundColor: null,
      //   scale: 2,
      // });
      // const link = document.createElement('a');
      // link.download = `campusos-${milestoneType}-${Date.now()}.png`;
      // link.href = canvas.toDataURL('image/png');
      // link.click();
      
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export image');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Copy share text
  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  // Native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My CamPassed Achievement',
          text: getShareText(),
          url: window.location.origin,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyText();
        }
      }
    } else {
      handleCopyText();
    }
  };
  
  // Social share (placeholder for future)
  const handleSocialShare = (platform) => {
    const config = SOCIAL_PLATFORMS[platform];
    if (!config) return;
    
    if (!config.available) {
      toast.info(`${config.name} sharing coming soon!`);
      return;
    }
    
    if (config.shareUrl) {
      window.open(config.shareUrl(getShareText()), '_blank', 'width=600,height=400');
    }
  };
  
  // Render the appropriate card
  const renderCard = () => {
    const commonProps = { ref: cardRef, size: cardSize, userName };
    
    switch (milestoneType) {
      case 'badge':
        return <BadgeEarnedCard {...commonProps} {...milestoneData} />;
      case 'rank':
        return <LeaderboardRankCard {...commonProps} {...milestoneData} />;
      case 'events':
        return <EventsMilestoneCard {...commonProps} {...milestoneData} />;
      case 'vip':
        return <VIPStatusCard {...commonProps} {...milestoneData} />;
      case 'passport':
        return <PassportMilestoneCard {...commonProps} {...milestoneData} />;
      default:
        return null;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="bg-[#0f0f17] rounded-2xl border border-white/[0.08] max-w-4xl w-full max-h-[90vh] overflow-hidden"
        data-testid="share-modal"
      >
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/20">
              <Share2 className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Share Your Achievement</h2>
              <p className="text-sm text-white/40">Show off your milestone!</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/[0.06] text-white/40 hover:text-white transition-colors"
            data-testid="close-share-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 md:p-6 grid md:grid-cols-2 gap-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Card Preview */}
          <div className="flex flex-col items-center">
            {/* Size selector */}
            <div className="flex gap-2 mb-4">
              {[
                { value: 'default', label: 'Standard' },
                { value: 'story', label: 'Story' },
                { value: 'square', label: 'Square' },
              ].map((size) => (
                <button
                  key={size.value}
                  onClick={() => setCardSize(size.value)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    cardSize === size.value
                      ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                      : 'bg-white/[0.03] text-white/50 border border-white/[0.06] hover:bg-white/[0.06]'
                  )}
                >
                  {size.label}
                </button>
              ))}
            </div>
            
            {/* Card */}
            <div className="transform scale-[0.65] md:scale-75 origin-top">
              {renderCard()}
            </div>
          </div>
          
          {/* Share Options */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div>
              <h3 className="text-sm font-medium text-white/60 mb-3 uppercase tracking-wider">Quick Actions</h3>
              <div className="grid grid-cols-3 gap-3">
                <ShareActionButton
                  icon={Download}
                  label="Save Image"
                  onClick={handleExport}
                  disabled={isExporting}
                  gradient="from-emerald-500 to-teal-500"
                  testId="save-image-btn"
                />
                <ShareActionButton
                  icon={copied ? Check : Copy}
                  label={copied ? 'Copied!' : 'Copy Text'}
                  onClick={handleCopyText}
                  gradient="from-blue-500 to-cyan-500"
                  testId="copy-text-btn"
                />
                <ShareActionButton
                  icon={Share2}
                  label="Share"
                  onClick={handleNativeShare}
                  gradient="from-violet-500 to-purple-500"
                  testId="native-share-btn"
                />
              </div>
            </div>
            
            {/* Social Platforms */}
            <div>
              <h3 className="text-sm font-medium text-white/60 mb-3 uppercase tracking-wider flex items-center gap-2">
                Social Platforms
                <span className="px-2 py-0.5 rounded-full bg-white/[0.05] text-xs text-white/40">Coming Soon</span>
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(SOCIAL_PLATFORMS).map(([key, platform]) => (
                  <ShareActionButton
                    key={key}
                    icon={platform.icon}
                    label={platform.name}
                    onClick={() => handleSocialShare(key)}
                    disabled={!platform.available}
                    gradient={platform.available ? platform.color : undefined}
                    testId={`share-${key}-btn`}
                  />
                ))}
              </div>
            </div>
            
            {/* Share Text Preview */}
            <div>
              <h3 className="text-sm font-medium text-white/60 mb-3 uppercase tracking-wider">Share Text</h3>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <p className="text-sm text-white/70 leading-relaxed">
                  {getShareText()}
                </p>
              </div>
            </div>
            
            {/* Pro tip */}
            <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-violet-300 font-medium">Pro Tip</p>
                  <p className="text-xs text-white/50 mt-1">
                    Take a screenshot of the card above and share it directly to your stories!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-white/[0.06] flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/10 text-white"
          >
            Close
          </Button>
          <Button
            onClick={handleNativeShare}
            className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
